'use client';

import type {
    ComponentType,
    HTMLAttributes,
    PropsWithChildren,
    ReactElement,
} from 'react';

import { Button } from '@/components/atoms/Button';
import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';
import type { MotionProps } from 'framer-motion';
import { motion } from 'framer-motion';

type CTAProps = {
    CTA?: unknown;
    CustomCTALink?: unknown;
    CTAOpenInNewTab?: unknown;
    CTAIcon?: unknown;
    CTAHoverIcon?: unknown;
    CTAIconVisible?: unknown;
    SecondaryButtonText?: unknown;
    SecondaryButtonLink?: unknown;
    SecondaryButtonOpenInNewTab?: unknown;
    SecondaryButtonIcon?: unknown;
    SecondaryButtonHoverIcon?: unknown;
    SecondaryButtonIconVisible?: unknown;
    OutlineButtonText?: unknown;
    OutlineButtonLink?: unknown;
    OutlineButtonOpenInNewTab?: unknown;
    OutlineButtonIcon?: unknown;
    OutlineButtonHoverIcon?: unknown;
    OutlineButtonIconVisible?: unknown;
    ThirdButtonText?: unknown;
    ThirdButtonLink?: unknown;
    ThirdButtonOpenInNewTab?: unknown;
    ThirdButtonIcon?: unknown;
    ThirdButtonHoverIcon?: unknown;
    ThirdButtonIconVisible?: unknown;
};

type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

type CTAOptions = {
    primaryVariant?: string;
    secondaryVariant?: string;
    outlineVariant?: string;
    buttonAlign?: string;
    wrapSpacing?: string;
    className?: string;
    buttonsClassName?: string;
    size?: unknown;
    primarySize?: unknown;
    secondarySize?: unknown;
    outlineSize?: unknown;
    enableAnim?: boolean;
    motionProps?: MotionProps;
    primaryButtonProps?: Record<string, unknown>;
    secondaryButtonProps?: Record<string, unknown>;
    outlineButtonProps?: Record<string, unknown>;
};

type CTAResult = {
    hasAny: boolean;
    element: ReactElement | null;
};

function valOf(value: unknown, fallback: unknown = ''): unknown {
    if (value == null) return fallback;
    if (typeof value === 'object' && value && 'value' in value) {
        return (value as { value?: unknown }).value ?? fallback;
    }
    return value;
}

const normalizeText = (value: unknown): string => {
    const normalized = valOf(value, '');
    if (normalized == null || typeof normalized === 'boolean') return '';
    if (typeof normalized === 'string') return normalized.trim();
    if (typeof normalized === 'number') return String(normalized);
    return '';
};

const normalizeSize = (val: unknown): ButtonSize => {
    const v = String(val ?? '').toLowerCase();
    if (v === 'sm' || v === 'small') return 'sm';
    if (v === 'lg' || v === 'large') return 'lg';
    if (
        v === 'xl' ||
        v === 'x-large' ||
        v === 'xlarge' ||
        v === 'extra-large' ||
        v === 'extralarge'
    ) {
        return 'xl';
    }
    return 'md';
};

const normalizeBoolean = (value: unknown, fallback = true): boolean => {
    if (value == null) return fallback;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return fallback;
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return fallback;
};

const mergeClassNames = (...values: Array<string | undefined>): string =>
    values.filter((val) => typeof val === 'string' && val.trim()).join(' ');

type MotionDivProps = Omit<HTMLAttributes<HTMLDivElement>, keyof MotionProps> &
    MotionProps;

const MotionDiv = motion.div as ComponentType<
    PropsWithChildren<MotionDivProps>
>;

export function getCTA(
    props: CTAProps = {},
    options: CTAOptions = {}
): CTAResult {
    const ButtonNode = Button as ComponentType<
        PropsWithChildren<Record<string, unknown>>
    >;

    const {
        primaryVariant = 'primary',
        secondaryVariant = 'secondary',
        outlineVariant = 'ghost',
        buttonAlign = 'justify-start',
        wrapSpacing = 'mt-10 lg:mt-14',
        className = '',
        buttonsClassName = 'flex flex-wrap items-center gap-4',
        size = 'md',
        primarySize,
        secondarySize,
        outlineSize,
        enableAnim = false,
        motionProps = {},
        primaryButtonProps = {},
        secondaryButtonProps = {},
        outlineButtonProps = {},
    } = options;

    const pText = normalizeText(props?.CTA);
    const pHref = normalizeText(props?.CustomCTALink);
    const pOpenInNewTab = valOf(props?.CTAOpenInNewTab, false);
    const pIcon = normalizeText(props?.CTAIcon);
    const pHoverIcon = normalizeText(props?.CTAHoverIcon);
    const pIconVisible = normalizeBoolean(valOf(props?.CTAIconVisible, true));
    const sText = normalizeText(props?.SecondaryButtonText);
    const sHref = normalizeText(props?.SecondaryButtonLink);
    const sOpenInNewTab = valOf(props?.SecondaryButtonOpenInNewTab, false);
    const sIcon = normalizeText(props?.SecondaryButtonIcon);
    const sHoverIcon = normalizeText(props?.SecondaryButtonHoverIcon);
    const sIconVisible = normalizeBoolean(
        valOf(props?.SecondaryButtonIconVisible, true)
    );
    const oText =
        normalizeText(props?.OutlineButtonText) ||
        normalizeText(props?.ThirdButtonText);
    const oHref =
        normalizeText(props?.OutlineButtonLink) ||
        normalizeText(props?.ThirdButtonLink);
    const oOpenInNewTab =
        valOf(props?.OutlineButtonOpenInNewTab, undefined) ??
        valOf(props?.ThirdButtonOpenInNewTab, false);
    const oIcon =
        normalizeText(props?.OutlineButtonIcon) ||
        normalizeText(props?.ThirdButtonIcon);
    const oHoverIcon =
        normalizeText(props?.OutlineButtonHoverIcon) ||
        normalizeText(props?.ThirdButtonHoverIcon);
    const oIconVisible = normalizeBoolean(
        valOf(
            props?.OutlineButtonIconVisible,
            valOf(props?.ThirdButtonIconVisible, true)
        )
    );

    const hasAny = Boolean(pText || sText || oText);
    if (!hasAny) return { hasAny: false, element: null };

    const defaultSize = normalizeSize(size);
    const primaryBtnSize = normalizeSize(primarySize || defaultSize);
    const secondaryBtnSize = normalizeSize(secondarySize || defaultSize);
    const outlineBtnSize = normalizeSize(outlineSize || defaultSize);

    const { className: primaryClassName, ...primaryRest } = primaryButtonProps;
    const { className: secondaryClassName, ...secondaryRest } =
        secondaryButtonProps;
    const { className: outlineClassName, ...outlineRest } = outlineButtonProps;

    const buttonsClass = mergeClassNames(
        buttonsClassName && buttonsClassName.trim()
            ? buttonsClassName
            : 'flex flex-wrap items-center gap-4',
        // Keep wrapped rows aligned with the CTA wrapper. Without this,
        // centered CTAs fall back to left alignment once they wrap on mobile.
        buttonAlign
    );
    const wrapClassName =
        `${wrapSpacing} flex items-center ${buttonAlign} ${className}`.trim();

    const buttons = (
        <div className={buttonsClass}>
            {pText &&
                (pHref ? (
                    <ButtonNode
                        asChild
                        variant={primaryVariant}
                        size={primaryBtnSize}
                        iconDefault={pIcon}
                        iconHover={pHoverIcon}
                        iconVisible={pIconVisible}
                        {...primaryRest}
                        className={mergeClassNames(
                            typeof primaryClassName === 'string'
                                ? primaryClassName
                                : undefined
                        )}
                    >
                        <a
                            href={pHref + ''}
                            {...buttonLinkTargetProps(pOpenInNewTab)}
                        >
                            {pText}
                        </a>
                    </ButtonNode>
                ) : (
                    <ButtonNode
                        variant={primaryVariant}
                        size={primaryBtnSize}
                        type="button"
                        iconDefault={pIcon}
                        iconHover={pHoverIcon}
                        iconVisible={pIconVisible}
                        {...primaryRest}
                        className={mergeClassNames(
                            'nj-lead',
                            typeof primaryClassName === 'string'
                                ? primaryClassName
                                : undefined
                        )}
                    >
                        {pText}
                    </ButtonNode>
                ))}

            {sText && (
                <ButtonNode
                    asChild
                    variant={secondaryVariant}
                    size={secondaryBtnSize}
                    iconDefault={sIcon}
                    iconHover={sHoverIcon}
                    iconVisible={sIconVisible}
                    {...secondaryRest}
                    className={mergeClassNames(
                        typeof secondaryClassName === 'string'
                            ? secondaryClassName
                            : undefined
                    )}
                >
                    <a
                        href={(sHref || '#') + ''}
                        {...buttonLinkTargetProps(sOpenInNewTab)}
                    >
                        {sText}
                    </a>
                </ButtonNode>
            )}

            {oText && (
                <ButtonNode
                    asChild
                    variant={outlineVariant}
                    size={outlineBtnSize}
                    iconDefault={oIcon}
                    iconHover={oHoverIcon}
                    iconVisible={oIconVisible}
                    {...outlineRest}
                    className={mergeClassNames(
                        typeof outlineClassName === 'string'
                            ? outlineClassName
                            : undefined
                    )}
                >
                    <a
                        href={(oHref || '#') + ''}
                        {...buttonLinkTargetProps(oOpenInNewTab)}
                    >
                        {oText}
                    </a>
                </ButtonNode>
            )}
        </div>
    );

    const element = enableAnim ? (
        <MotionDiv {...motionProps} className={wrapClassName}>
            {buttons}
        </MotionDiv>
    ) : (
        <div className={wrapClassName}>{buttons}</div>
    );

    return { hasAny: true, element };
}

export function CTAsFromProps(
    props: CTAProps = {},
    opts?: CTAOptions
): ReactElement | null {
    const { element } = getCTA(props, opts);
    return element;
}
